import { theme } from "@monthly/theme";
import HighchartsReact from "highcharts-react-official";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { addWatermarkToHighchartsExport } from "../chartWatermark";
import Highcharts from "../HighChartsConfig";
import {
  formatNumberWithSeparators,
  formatXAxisLabel,
  truncateNumber,
} from "../utils/formatting";

// KPI-specific colors for the second bar in each pair
const KPI_COLORS: Record<string, string> = {
  Ingreso: theme.palette.primary.main,
  Costo: theme.palette.redc.main,
  Gasto: theme.palette.yellowc.main,
  EBITDA: theme.palette.accent.main,
  "Otras cuentas": theme.palette.secondary.main,
  "Ut Neta": theme.palette.accent.main,
  "Utilidad Neta": theme.palette.accent.main,
  "Activo Circulante": theme.palette.greenc.main,
  "Activo Largo Plazo": theme.palette.greenc.main,
  "Pasivo Circulante": theme.palette.orangec.main,
  "Pasivo Largo Plazo": theme.palette.orangec.main,
  Capital: theme.palette.orangec.main,
};

const LIGHT_GRAY = theme.palette.support.main;

interface ColumnChartProps {
  categories: string[];
  series: {
    name: string;
    data: number[];
    color?: string;
  }[];
  height?: number;
  marginTop?: number;
  stacked?: boolean;
  showLegend?: boolean;
  useKpiColors?: boolean;
  showStackedLabels?: boolean;
  showSegmentLabels?: boolean;
  showDataLabels?: boolean;
  isPercentage?: boolean;
  decimalPlaces?: number;
  customStackTotals?: number[]; // Optional array of custom totals for each category
  colorByValue?: {
    positiveColor: string;
    negativeColor: string;
  };
  seriesWithLabels?: string[]; // Array of series names that should show segment labels
  units?: string;
}

export interface ColumnChartRef {
  downloadImage: (filename?: string) => void;
}

export const ColumnChart = forwardRef<ColumnChartRef, ColumnChartProps>(
  (
    {
      categories,
      series,
      height = 250,
      marginTop,
      stacked = false,
      showLegend = true,
      useKpiColors = false,
      showStackedLabels = false,
      showSegmentLabels = true,
      showDataLabels = false,
      isPercentage = false,
      decimalPlaces = 1,
      customStackTotals,
      colorByValue,
      seriesWithLabels,
      units,
    },
    ref,
  ) => {
    const finalDecimalPlaces = decimalPlaces !== undefined ? decimalPlaces : 1;
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    useImperativeHandle(ref, () => ({
      downloadImage: (filename = "chart") => {
        if (chartRef.current?.chart) {
          addWatermarkToHighchartsExport(chartRef.current.chart, filename);
        }
      },
    }));

    // When useKpiColors is true, color each point based on category
    const processedSeries = series.map((s, seriesIndex) => {
      // Truncate all data values
      const truncatedData = s.data.map((value) =>
        truncateNumber(value, decimalPlaces),
      );

      // If colorByValue is provided, color each bar based on positive/negative value
      if (colorByValue) {
        return {
          type: "column" as const,
          name: s.name,
          data: truncatedData.map((value) => ({
            y: value,
            color:
              value >= 0
                ? colorByValue.positiveColor
                : colorByValue.negativeColor,
          })),
        };
      }

      if (useKpiColors && seriesIndex === 1) {
        // Second series: use KPI-specific colors per category
        return {
          type: "column" as const,
          name: s.name,
          data: truncatedData.map((value, idx) => ({
            y: value,
            color: KPI_COLORS[categories[idx]] || "blue",
          })),
        };
      } else if (useKpiColors && seriesIndex === 0) {
        // First series: light gray
        return {
          type: "column" as const,
          name: s.name,
          data: truncatedData,
          color: LIGHT_GRAY,
        };
      }

      // For stacked charts with labels
      if (showStackedLabels && stacked) {
        // Determine if this specific series should show labels
        // showSegmentLabels is the global on/off switch
        // seriesWithLabels acts as an additional filter when showSegmentLabels is true
        const shouldShowLabels =
          showSegmentLabels &&
          (seriesWithLabels ? seriesWithLabels.includes(s.name) : true);

        return {
          type: "column" as const,
          name: s.name,
          data: truncatedData,
          color:
            s.color ||
            ["blue", "#22C55E", "#F59E0B", "#8B5CF6"][seriesIndex % 4],
          dataLabels: {
            enabled: shouldShowLabels,
            formatter: function () {
              const value = truncateNumber(
                (this as { y?: number }).y ?? 0,
                decimalPlaces,
              );
              return isPercentage
                ? `${formatNumberWithSeparators(value, finalDecimalPlaces)}%`
                : formatNumberWithSeparators(value, finalDecimalPlaces);
            },
            inside: true,
            verticalAlign: "middle" as const,
            y: 0,
            align: "center" as const,
            style: {
              fontFamily: `${theme.typography.caption.fontFamily}`,
              fontSize: `${theme.typography.caption.fontSize}`,
              fontWeight: `${theme.typography.caption.fontWeight}`,
              color: "#FFFFFF",
              textOutline: "none",
            },
          },
        };
      }

      return {
        type: "column" as const,
        name: s.name,
        data: truncatedData,
        color:
          s.color || ["blue", "#22C55E", "#F59E0B", "#8B5CF6"][seriesIndex % 4],
      };
    });

    const options: Highcharts.Options = {
      chart: {
        type: "column",
        height,
        marginTop,
      },
      title: {
        text: undefined,
      },
      tooltip: {
        shared: false,
        useHTML: true,
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        borderRadius: 4,
        padding: 6,
        style: {
          color: theme.palette.text.primary,
        },
        formatter: function () {
          const categoryName = this.key;
          const seriesName = this.series.name;
          const value = this.y;
          const seriesColor = this.color;
          const suffix = units ? ` ${units}` : "";

          const fontSize = theme.typography.overline.fontSize;
          const fontWeight = theme.typography.overline.fontWeight;
          const fontWeightSemiBold = theme.typography.fontWeightSemiBold;
          const letterSpacing = theme.typography.overline.letterSpacing;
          const primaryColor = theme.palette.text.primary;
          const secondaryColor = theme.palette.secondary[400];

          return `
            <div>
              <div style="font-size: ${fontSize}; font-weight: ${fontWeightSemiBold}; letter-spacing: ${letterSpacing}; color: ${primaryColor}; margin-bottom: 4px;">
              ${categoryName}
              </div>
              <div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 8px; height: 8px; background-color: ${seriesColor}; border-radius: 50%; margin-right: 3px;"></span>
              <span style="font-size: ${fontSize}; font-weight: ${fontWeight}; color: ${secondaryColor};">
                ${seriesName}
              </span>
              <span style="font-size: ${fontSize}; font-weight: ${fontWeight}; color: ${primaryColor};">
               &nbsp;- ${value}${suffix}
              </span>
              </div>
            </div>
            `;
        },
      },
      xAxis: {
        categories,
        crosshair: true,
        labels: {
          useHTML: true,
          formatter: function () {
            return formatXAxisLabel(String(this.value));
          },
          style: {
            ...theme.typography.caption,
            color: theme.palette.text.primary,
          },
        },
        lineColor: theme.palette.text.primary,
      },
      yAxis: {
        reversedStacks: false,
        title: {
          text: undefined,
        },
        labels: {
          enabled: false,
        },
        gridLineWidth: 0,
        lineWidth: 0,
        stackLabels: {
          enabled: showStackedLabels && stacked,
          formatter: function () {
            const context = this as { total?: number; x?: number };
            // Use custom total if provided, otherwise fall back to calculated total
            const value =
              customStackTotals && context.x !== undefined
                ? (customStackTotals[context.x] ?? context.total ?? 0)
                : (context.total ?? 0);
            // Truncate instead of rounding
            const truncatedValue = truncateNumber(value, finalDecimalPlaces);
            return isPercentage
              ? `${formatNumberWithSeparators(truncatedValue, finalDecimalPlaces)}%`
              : formatNumberWithSeparators(truncatedValue, finalDecimalPlaces);
          },
          style: {
            fontFamily: `${theme.typography.caption.fontFamily}`,
            fontSize: `${theme.typography.caption.fontSize}`,
            fontWeight: `${theme.typography.caption.fontWeight}`,
            color: `${theme.palette.text.primary}`,
          },
          y: -5,
        },
      },
      legend: {
        enabled: showLegend,
        align: "right",
        verticalAlign: "top",
        floating: true,
        y: -10,
        symbolRadius: 10,
        itemStyle: {
          fontFamily: `${theme.typography.caption.fontFamily}`,
          fontSize: `${theme.typography.caption.fontSize}`,
          fontWeight: `${theme.typography.caption.fontWeight}`,
          color: `${theme.palette.text.primary}`,
        },
      },
      plotOptions: {
        column: {
          stacking: stacked ? "normal" : undefined,
          pointPadding: 0.05,
          groupPadding: 0.15,
          borderRadius: 4,
          borderWidth: 0,
          dataLabels: {
            enabled: useKpiColors || showDataLabels,
            formatter: function () {
              const value = (this as { y?: number }).y ?? 0;
              const truncated = truncateNumber(value, finalDecimalPlaces);
              const formatted = truncated.toLocaleString("en-US", {
                minimumFractionDigits: finalDecimalPlaces,
                maximumFractionDigits: finalDecimalPlaces,
              });
              return isPercentage ? `${formatted}%` : formatted;
            },
            style: {
              fontFamily: `${theme.typography.caption.fontFamily}`,
              fontSize: `${theme.typography.caption.fontSize}`,
              fontWeight: `${theme.typography.caption.fontWeight}`,
              color: theme.palette.text.primary,
              textOutline: "none",
            },
            y: -5,
          },
        },
      },
      series: processedSeries,
    };

    useEffect(() => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.reflow();
      }
    }, []);

    return (
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
      />
    );
  },
);

ColumnChart.displayName = "ColumnChart";
